import React from 'react';
import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { EmptyState, Panel } from '../../../ui-kit';
import {
  fetchCcConfiguration, type CcConfiguration,
} from '../../../services/carrierCorrespondenceService';
import { useLive } from '../rocco/live';
import CarriersPanel from '../CarriersPanel';
import VoiceAgents from '../VoiceAgents';

/**
 * How the agent is set up.
 *
 * Almost everything here is read from the running system, so the page cannot
 * promise something the calls do not do. The one exception is the block at the
 * top — how a stalled case would be found in a real book — which is a setting
 * we have not built yet, and says so on its face rather than pretending.
 */
export default function ConfigurationPage() {
  const { data, error, loading } = useLive(fetchCcConfiguration, 60_000);
  if (error && !data) return <EmptyState message={error} />;

  return (
    <Stack spacing={2}>
      <StalledSetting data={data} loading={loading} />

      <Panel
        title="How a case moves"
        subtitle="From sitting too long to knowing what is outstanding"
        loading={loading}
        loadingHeight={280}
      >
        <Stack>
          {(data?.rules ?? []).map((rule) => (
            <Box
              key={rule.when}
              sx={(t) => ({
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '220px 1fr' },
                columnGap: 2, rowGap: 0.25, py: 1.25,
                borderBottom: `1px solid ${t.tokens.border.subtle}`,
                '&:last-of-type': { borderBottom: 'none' },
              })}
            >
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
                {rule.when}
              </Typography>
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>
                  {rule.then}
                </Typography>
                <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                  {rule.detail}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Panel>

      <VoiceAgents
        agents={data?.agents ?? []}
        facts={data?.agent_facts ?? []}
        providerError={data?.provider_error}
        loading={loading}
      />

      {/* The same layout as ROCCO's and Policy Servicing's: what each call
          finds out beside the guardrails. */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
        <Panel title="What each call finds out" subtitle="Read from the conversation when the call ends, so nobody types it in"
          loading={loading} loadingHeight={220}>
          <Stack spacing={1.25}>
            {(data?.finds_out ?? []).map((item) => <Line key={item.key} title={item.label} detail={item.detail} />)}
          </Stack>
        </Panel>

        <Panel title="Guardrails" subtitle="Held on every call, whoever asks" loading={loading} loadingHeight={220}>
          <Stack spacing={1.25}>
            {(data?.guardrails ?? []).map((rail) => <Line key={rail.title} title={rail.title} detail={rail.detail} />)}
          </Stack>
        </Panel>
      </Box>

      {/* Last, and folded: the same table Policy Servicing shows, and the same
          rows — a number set here is the number every agent dials. */}
      <CarriersPanel />
    </Stack>
  );
}

/**
 * What we mean by stalled.
 *
 * Shown as a setting, and it will be one — but it is not wired to anything yet,
 * and the panel says so rather than letting a dropdown imply it does something.
 * The cases in this demo are a fixed set; changing this number moves none of
 * them, and finding out by trying would be a worse way to learn it.
 */
function StalledSetting({ data, loading }: { data: CcConfiguration | null; loading: boolean }) {
  const settings = data?.settings;
  const [days, setDays] = React.useState<number | ''>('');
  const value = days === '' ? settings?.stalled_after_days ?? 5 : days;

  return (
    <Panel
      title="What counts as stalled"
      subtitle="The rule the agent watches your book against"
      loading={loading}
      loadingHeight={160}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Typography variant="body1" sx={(t) => ({ color: t.tokens.text.primary })}>
            A case is stalled when it has been in the same state for more than
          </Typography>
          <Select
            size="small"
            value={value}
            onChange={(event) => setDays(Number(event.target.value))}
            sx={{ minWidth: 88 }}
            inputProps={{ 'aria-label': 'Days in the same state before a case counts as stalled' }}
          >
            {(settings?.stalled_after_choices ?? [3, 5, 7, 10, 14]).map((choice) => (
              <MenuItem key={choice} value={choice}>{choice}</MenuItem>
            ))}
          </Select>
          <Typography variant="body1" sx={(t) => ({ color: t.tokens.text.primary })}>
            days.
          </Typography>
        </Stack>

        <Typography variant="body1" sx={(t) => ({ color: t.tokens.text.primary })}>
          Once it is, we chase the carrier up to{' '}
          <strong>{settings?.chase_times ?? 3} times</strong>, leaving{' '}
          <strong>{settings?.chase_every_days ?? 2} days</strong> between calls, and stop as soon as
          they tell us what is outstanding.
        </Typography>

      </Stack>
    </Panel>
  );
}

function Line({ title, detail }: { title: string; detail: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{title}</Typography>
      <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{detail}</Typography>
    </Stack>
  );
}
